import React, { useState } from 'react';
import { Upload, CheckCircle, X, ArrowLeft, Plus } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import axios from 'axios';
import { getAuthHeader } from '../../services/otpAuthService';

interface DocumentoEntry {
  id: string;
  nome: string;
  file: File | null;
}

interface StepUploadDocumentiProps {
  attoId: string;
  onBack: () => void;
  onSuccess: () => void;
}

let entryCounter = 0;

const StepUploadDocumenti: React.FC<StepUploadDocumentiProps> = ({ attoId, onBack, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const [documenti, setDocumenti] = useState<DocumentoEntry[]>([
    { id: `doc_${++entryCounter}`, nome: '', file: null }
  ]);

  const API_BASE_URL = process.env.REACT_APP_API_URL
    ? process.env.REACT_APP_API_URL.replace('/graphql', '')
    : 'http://localhost:5000';

  const addDocumento = () => {
    setDocumenti(prev => [...prev, { id: `doc_${++entryCounter}`, nome: '', file: null }]);
  };

  const removeDocumento = (id: string) => {
    if (documenti.length <= 1) return;
    setDocumenti(prev => prev.filter(d => d.id !== id));
  };

  const updateNome = (id: string, nome: string) => {
    setDocumenti(prev => prev.map(d => d.id === id ? { ...d, nome } : d));
  };

  const updateFile = (id: string, file: File | null) => {
    if (file) {
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Solo file PDF, JPG, JPEG, PNG sono permessi');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Il file e troppo grande. Dimensione massima: 10MB');
        return;
      }
    }
    setDocumenti(prev => prev.map(d => d.id === id ? { ...d, file } : d));
  };

  const handleUpload = async () => {
    const documentiValidi = documenti.filter(d => d.file);
    if (documentiValidi.length === 0) {
      toast.error('Devi caricare almeno un documento');
      return;
    }

    // Verifica che ogni documento con file abbia un nome
    for (const doc of documentiValidi) {
      if (!doc.nome.trim()) {
        toast.error('Assegna un nome a tutti i documenti');
        return;
      }
    }

    setLoading(true);
    try {
      const formData = new FormData();
      const nomiDocumenti: string[] = [];

      for (const doc of documentiValidi) {
        formData.append('documenti', doc.file!);
        nomiDocumenti.push(doc.nome.trim());
      }
      formData.append('nomiDocumenti', JSON.stringify(nomiDocumenti));

      const response = await axios.post(
        `${API_BASE_URL}/api/atti-svincolo/${attoId}/upload-documenti`,
        formData,
        {
          headers: {
            ...getAuthHeader(),
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        onSuccess();
      } else {
        toast.error(response.data.message || 'Errore durante il caricamento');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Errore durante il caricamento dei documenti');
    } finally {
      setLoading(false);
    }
  };

  const documentiConFile = documenti.filter(d => d.file);
  const tuttiConNome = documentiConFile.every(d => d.nome.trim());

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-base font-bold text-gray-900 dark:text-white mb-1">
          Carica Documenti
        </h2>
        <p className="text-xs text-gray-600 dark:text-gray-400">
          Carica i documenti necessari per la pratica. Puoi caricare piu file o un unico PDF con tutte le pagine.
        </p>
      </div>

      {/* Info */}
      <div className="bg-blue-50 dark:bg-blue-900/15 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
        <p className="text-xs text-blue-800 dark:text-blue-200">
          <strong>Suggerimento:</strong> Puoi caricare carta di circolazione, documento identita venditore/acquirente separatamente oppure in un unico file.
          Assegna un nome descrittivo a ogni documento. Formati: PDF, JPG, PNG (max 10MB).
        </p>
      </div>

      {/* Lista documenti */}
      <div className="space-y-2.5">
        {documenti.map((doc, index) => (
          <div key={doc.id} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3.5">
            <div className="flex items-start gap-2.5">
              <div className="flex-1 space-y-2">
                {/* Nome documento */}
                <input
                  type="text"
                  value={doc.nome}
                  onChange={(e) => updateNome(doc.id, e.target.value)}
                  placeholder="Nome documento (es: Carta di circolazione, Documenti completi...)"
                  className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:text-white placeholder:text-gray-400"
                />

                {/* File selector */}
                <div className="flex items-center gap-2">
                  <input
                    type="file"
                    id={`file-${doc.id}`}
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => updateFile(doc.id, e.target.files?.[0] || null)}
                    className="hidden"
                  />
                  <label
                    htmlFor={`file-${doc.id}`}
                    className={`flex-1 px-3 py-2 border border-dashed rounded-lg cursor-pointer transition-colors ${
                      doc.file
                        ? 'border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/10'
                        : 'border-gray-300 dark:border-gray-600 hover:border-purple-400 dark:hover:border-purple-500'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {doc.file ? (
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                      ) : (
                        <Upload className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      )}
                      <div className="min-w-0 flex-1">
                        {doc.file ? (
                          <p className="text-xs text-gray-900 dark:text-white truncate">
                            {doc.file.name} <span className="text-gray-400">({(doc.file.size / 1024 / 1024).toFixed(1)} MB)</span>
                          </p>
                        ) : (
                          <p className="text-xs text-gray-500 dark:text-gray-400">Seleziona file...</p>
                        )}
                      </div>
                    </div>
                  </label>
                  {doc.file && (
                    <button
                      type="button"
                      onClick={() => updateFile(doc.id, null)}
                      className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                      title="Rimuovi file"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>

              {/* Rimuovi riga */}
              {documenti.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeDocumento(doc.id)}
                  className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors mt-1"
                  title="Rimuovi documento"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Aggiungi documento */}
      <button
        type="button"
        onClick={addDocumento}
        className="w-full px-3 py-2 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-600 dark:text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 hover:border-purple-400 dark:hover:border-purple-500 transition-colors flex items-center justify-center gap-1.5"
      >
        <Plus className="w-4 h-4" />
        Aggiungi altro documento
      </button>

      {/* Bottoni */}
      <div className="flex justify-between pt-3 border-t border-gray-200 dark:border-gray-700">
        <button
          type="button"
          onClick={onBack}
          disabled={loading}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors flex items-center gap-1.5 disabled:opacity-50"
        >
          <ArrowLeft className="w-4 h-4" />
          Indietro
        </button>
        <button
          type="button"
          onClick={handleUpload}
          disabled={loading || documentiConFile.length === 0 || !tuttiConNome}
          className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              Caricamento...
            </>
          ) : (
            <>
              <Upload className="w-4 h-4" />
              Carica {documentiConFile.length > 0 ? `(${documentiConFile.length})` : ''}
            </>
          )}
        </button>
      </div>

      {/* Info scadenza */}
      <p className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2">
        I documenti saranno disponibili per lo studio selezionato per 48 ore dalla data dell'appuntamento.
      </p>
    </div>
  );
};

export default StepUploadDocumenti;
